import {Component, Inject, OnInit} from '@angular/core';
import {BrowserSessionService} from '@webmate-web-core/shared/services/browsersession.service';
import {ArtifactId, AuditId, DeviceId, ExpeditionId, FindingId, ProjectId, ScreenshotId, SpecId} from '@webmate-web-core/shared/types/ids';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ConsoleInfo, WORKBENCH_VIEW_CONFIG, WorkbenchContext, WorkbenchViewConfig} from '../../../shared/types';
import {faHourglassStart, faPlus} from '@fortawesome/pro-light-svg-icons';
import {Artifact, ArtifactType, WellKnownArtifactTypes} from '@webmate-web-core/shared/types/artifact';
import {ContentCell, ContentCellId, FactRequest, FindingCreationData, ObservationTarget, Spec} from '@webmate-web-core/shared/types/testmgmt';
import {getRandomUUID} from '@webmate-web-core/shared/utils/utils';
import {WmNotificationService} from '@webmate-web-core/shared/components/notification/wm-notification.service';
import {WorkbenchTestService} from '../../../shared/services/workbench-test.service';
import {distinctUntilChanged, filter, finalize, map, mergeMap, switchMap, take, tap, timeout} from 'rxjs/operators';
import {BehaviorSubject, forkJoin, Observable, of, throwError} from 'rxjs';
import {ArtifactService} from '@webmate-web-core/shared/services/artifact.service';
import {
    faCheckCircle,
    faExclamationCircle,
    faFilter,
    faGlobe,
    faInfoCircle,
    faMinusCircle,
    faSparkles,
    faTimesCircle
} from '@fortawesome/pro-solid-svg-icons';
import {WorkbenchExpeditionService} from '../../../shared/services/workbench-expedition.service';
import {
    BrowserSessionInfo,
    ElementDescriptor,
    ElementSelector
} from '@webmate-web-core/shared/types/browsersession';
import {WorkbenchViewComponent} from '../../../shared/components/viewcontainers/workbench-view.component';
import {ImageService} from '@webmate-web-core/shared/services/image.service';
import {StateInfoItem} from '@webmate-web-core/shared/components/state/stateinfoitemrendererfactory';
import {HighlightedElement} from '@webmate-web-core/shared/components/state/highlightedElement.stateinfo';
import {ImageMetadata} from '@webmate-web-core/shared/api/rest/image.restapi';
import {AssessmentTask, AuditActivity} from '@webmate-web-core/shared/services/audit.service';
import {TestService} from '@webmate-web-core/shared/services/test.service';
import {Tag} from '@webmate-web-core/shared/types/tag';
import {WorkbenchLayoutManager} from '../../../shared/services/workbench-layout-manager.service';
import {WorkbenchContextService} from '../../../shared/services/workbench-context.service';
import {WorkbenchKeyValStore} from '../../../shared/services/workbench-store.service';
import {AuditInfo, AuditSession} from '../../../shared/services/audit-session';
import {WorkbenchAuditService} from '../../../shared/services/workbench-audit.service';
import {mapSpecLabelsToAxeLabels} from '@webmate-web-core/shared/services/specs';
import {IssueSeverity} from '../../../../webmate/shared/console/types/shared';
import {DeviceService} from '@webmate-web-core/shared/services/device/device.service';
import {Device} from '@webmate-web-core/shared/types/devices/device';
import {DevicePropertyNames} from '@webmate-web-core/shared/types/devices/device-properties';
import {Browser, browserFromBackendObject} from '@webmate-web-core/shared/types/browser';
import {FactGenerationMode, FactId, FactSessionInfo, ScopedFactRequest} from '@webmate-web-core/shared/types/fact';
import {FactService} from '@webmate-web-core/shared/services/fact.service';


export interface CDPScreenshotInfo {
    screenshot: string; // base64 encoded png
    nodeId: number;
    boundingRect: any;
}

export interface WcagAnalysisResults {
    violations: any[];
    inapplicable: any[];
    passes: any[];
}

interface SelectOption {
    label: string;
    value: string;
}

interface AnalysisProgress {
    message?: string;
    percentage: number;
}

interface MobileAnalysisExpedition {
    expedition: BrowserSessionInfo;
    ownedByAnalysis: boolean;
}

@UntilDestroy()
@Component({
    templateUrl: './wcag-analysis-view.component.html',
    styleUrls: ['./wcag-analysis-view.component.less'],
})
export class WCAGAnalysisViewComponent extends WorkbenchViewComponent implements OnInit {
    private static readonly MINIMUM_IOS_ANALYSIS_VERSION = 17;

    id: string;
    projectId: ProjectId;

    auditInfo?: AuditInfo;
    activeExpedition?: BrowserSessionInfo;
    activeDeviceId?: DeviceId;
    activeDevice?: Device;

    activeConsole: ConsoleInfo = null;

    forceHaltAnalysis = new BehaviorSubject(false);

    reportData: any;

    filteredObservations: WcagAnalysisResults;

    observations: WcagAnalysisResults;

    stats: any;

    analyzing: boolean = false;
    analysisError: string;

    activeTarget: any;
    activeObservation: any;
    activeObservationAssessmentTasks?: AssessmentTask[];

    activeElement?: ElementDescriptor;
    activeElementStateInfos?: StateInfoItem[];
    activeElementScreenshotMeta?: ImageMetadata;

    activeAuditId: AuditId | undefined;
    activeAuditActivity: AuditActivity | undefined;
    activeAuditActivitySpec: Spec | undefined;
    activeTargetFailureSummary: string;

    analysisUrl: string | undefined;
    artifactId?: ArtifactId;

    filterObservationsForAuditActivity: boolean = false;

    auditSession?: AuditSession;

    addFindingPending: boolean = false;

    // Session-local dedup: keys of observations (axe rule id + representative target selector)
    // already converted into a Finding, individually or via a bulk action. Cleared on each new analysis.
    private addedObservationKeys = new Set<string>();
    isObservationAdded = (rule: any): boolean => this.addedObservationKeys.has(this.getObservationKey(rule));

    bulkAddPending: { violations?: boolean; passes?: boolean; inapplicable?: boolean; all?: boolean } = {};

    isAuditMode: boolean = false;
    hasAuditContext: boolean = false;
    canUseAuditFilters: boolean = false;
    canShowRelatedTasks: boolean = false;

    analysisProgress?: AnalysisProgress;
    displayedAnalysisArtifactType?: ArtifactType;
    factSessionInfo?: FactSessionInfo;

    private preparingMobileExpedition = false;
    private analysisDeviceId?: DeviceId;
    private analysisExpeditionId?: ExpeditionId;
    private temporaryMobileExpeditionId?: ExpeditionId;
    private restoringAnalysisExpedition = false;
    private restoredAnalysisExpeditionId?: ExpeditionId;

    readonly modeOptions = [
        {label: 'Quick', value: 'quick', icon: faSparkles},
        {label: 'Complete', value: 'complete', icon: faHourglassStart},
        {label: 'Custom', value: 'custom', icon: faFilter}
    ];
    analysisMode: string = 'quick';

    readonly a11yLevelOptions = [
        {label: 'A', value: 'A'},
        {label: 'AA', value: 'AA'},
        {label: 'AAA', value: 'AAA'}
    ];
    selectedA11yLevels: string[] = [];

    readonly languageOptions: SelectOption[] = [
        {label: 'EN', value: 'en'},
        {label: 'DE', value: 'de'},
        {label: 'FR', value: 'fr'},
    ];
    selectedLanguage: string = 'en';

    activityOptions: SelectOption[] = [];
    selectedActivities: string[] = [];

    get isMobileAnalysis(): boolean {
        if (this.activeDeviceId) {
            return this.activeDevice?.id === this.activeDeviceId
                && this.activeDevice.platform.getPlatformFamily() === 'mobile';
        }
        const browser = this.activeExpedition?.browser;
        if (!browser) {
            return false;
        }

        const hydratedBrowser = browser instanceof Browser ? browser : browserFromBackendObject(browser);
        return hydratedBrowser.isMobile();
    }

    get hasRequiredAnalysisContext(): boolean {
        return this.isMobileAnalysis
            ? !!this.activeDeviceId && !!this.activeConsole
            : !!this.activeExpedition;
    }

    get mobileAnalysisUnavailableMessage(): string | undefined {
        if (!this.isMobileAnalysis || this.activeDevice?.platform.platformType !== 'iOS') {
            return undefined;
        }

        const platformVersion = this.activeDevice.platform.platformVersion;
        const majorVersion = Number.parseInt(platformVersion, 10);
        if (Number.isFinite(majorVersion)
            && majorVersion >= WCAGAnalysisViewComponent.MINIMUM_IOS_ANALYSIS_VERSION) {
            return undefined;
        }

        const versionDetails = Number.isFinite(majorVersion)
            ? `The active device is running iOS ${platformVersion}.`
            : 'The active device\'s iOS version could not be determined.';
        return `Mobile analysis requires iOS ${WCAGAnalysisViewComponent.MINIMUM_IOS_ANALYSIS_VERSION}.0 or later. ${versionDetails}`;
    }

    get canStartAnalysis(): boolean {
        return this.hasRequiredAnalysisContext && !this.mobileAnalysisUnavailableMessage;
    }

    private get activeAnalysisArtifactType(): ArtifactType {
        return this.isMobileAnalysis
            ? WellKnownArtifactTypes.MobileAccessibilityAnalysis
            : WellKnownArtifactTypes.ContentAnalysis;
    }

    supportsInteractiveDomHighlight(): boolean {
        return (this.displayedAnalysisArtifactType || this.activeAnalysisArtifactType) === WellKnownArtifactTypes.ContentAnalysis;
    }

    constructor(@Inject(WORKBENCH_VIEW_CONFIG) public config: WorkbenchViewConfig<any>,
                private browserSessionService: BrowserSessionService, private artifactService: ArtifactService,
                private factService: FactService,
                private workbenchTestService: WorkbenchTestService,
                private testService: TestService,
                private imageService: ImageService,
                private store: WorkbenchKeyValStore,
                layoutManager: WorkbenchLayoutManager,
                private workbenchExpeditionService: WorkbenchExpeditionService,
                private readonly deviceService: DeviceService,
                private workbenchContextService: WorkbenchContextService,
                private readonly workbenchAuditService: WorkbenchAuditService,
                private readonly notificationService: WmNotificationService) {
        super(layoutManager, config);
    }

    ngOnInit() {
        this.projectId = this.workbenchContextService.getProjectId();

        this.workbenchContextService.subscribeWorkbenchContext$().pipe(
            map(context => context.deviceContext?.activeDevice),
            distinctUntilChanged(),
            switchMap(deviceId => deviceId ? this.deviceService.getDevice$(deviceId).pipe(take(1)) : of(undefined)),
            untilDestroyed(this)
        ).subscribe(device => this.activeDevice = device);

        this.workbenchContextService.subscribeWorkbenchContext$().pipe(
            switchMap(context => {
                this.updateAuditContextState(context);

                if (this.hasAuditContext && this.activeAuditActivity?.assessedSpec) {
                    return this.testService.getSpecs$([this.activeAuditActivity.assessedSpec]).pipe(take(1)).pipe(map(spec => ({context, spec})));
                } else {
                    return of({context, spec: undefined});
                }
            }),
            switchMap(({context, spec}) => {
                if (this.hasAuditContext && this.auditSession) {
                    return this.auditSession.getAuditInfo$().pipe(map(auditInfo => ({context, spec, auditInfo})));
                } else {
                    return of({context, spec, auditInfo: undefined});
                }
            })
        ).subscribe(({spec, context, auditInfo}) => {
            const nextDeviceId = context.deviceContext?.activeDevice;
            if (this.analyzing && this.analysisDeviceId && nextDeviceId !== this.analysisDeviceId) {
                this.forceHaltAnalysis.next(true);
            }
            this.activeDeviceId = nextDeviceId;

            if (this.activeExpedition?.id != context.expeditionContext?.activeExpedition?.id) {
                this.activeExpedition = context.expeditionContext?.activeExpedition;
                const isAnalysisCleanupRestore = this.restoringAnalysisExpedition
                    && this.activeExpedition?.id === this.restoredAnalysisExpeditionId;
                if (!isAnalysisCleanupRestore) {
                    this.analysisError = '';
                }
                this.restoringAnalysisExpedition = false;
                this.restoredAnalysisExpeditionId = undefined;

                // Halting of analysis if activeExpedition is changed
                if (this.analyzing && !this.preparingMobileExpedition
                    && this.activeExpedition?.id !== this.analysisExpeditionId) {
                    this.forceHaltAnalysis.next(true);
                }
            }
            this.activeConsole = context.deviceContext?.activeConsole;
            this.auditInfo = auditInfo;
            this.updateActivityOptions(this.hasAuditContext ? this.auditInfo?.specs : undefined);

            this.activeAuditActivitySpec = this.canUseAuditFilters ? spec?.[0] : undefined;
            this.updateFilter(this.filterObservationsForAuditActivity);
            this.updateActiveObservationAssessmentTasks();
        });

        this.store.subscribeToValue('TODO.shownA11yReport').subscribe((artifactId: ArtifactId) => {
            // Halt Analysis if historical artifact viewed
            if (this.analyzing) {
                this.forceHaltAnalysis.next(true);
            }
            this.onArtifactUpdate(undefined, artifactId);
        });
    }

    updateAuditContextState(context: WorkbenchContext): void {
        this.isAuditMode = context.activeMode.kind === 'audit';
        this.hasAuditContext = this.isAuditMode && !!context.auditContext?.activeAudit;
        this.activeAuditId = this.hasAuditContext ? context.auditContext?.activeAudit : undefined;
        this.activeAuditActivity = this.hasAuditContext ? context.auditContext?.activeAuditActivity : undefined;
        this.canUseAuditFilters = this.hasAuditContext && !!this.activeAuditActivity;
        this.canShowRelatedTasks = this.hasAuditContext;

        if (!this.canUseAuditFilters) {
            this.filterObservationsForAuditActivity = false;
            this.activeAuditActivitySpec = undefined;
        }

        if (this.hasAuditContext && this.activeAuditId) {
            this.auditSession = this.workbenchAuditService.getOrCreateAuditSession(this.activeAuditId);
            return;
        }

        this.clearAuditOnlyState();
    }

    private clearAuditOnlyState(): void {
        this.auditSession = undefined;
        this.auditInfo = undefined;
        this.activeAuditId = undefined;
        this.activeAuditActivity = undefined;
        this.activeAuditActivitySpec = undefined;
        this.activityOptions = [];
        this.selectedActivities = [];
        this.filterObservationsForAuditActivity = false;
        this.activeObservationAssessmentTasks = undefined;
    }

    onArtifactUpdate(reportData: any, artifactId: ArtifactId, artifactType?: ArtifactType) {
        if (!artifactId) {
            return;
        }

        if (this.artifactId !== artifactId) {
            this.clearObservationSelection();
            this.artifactId = artifactId;
        }

        // A new result set is about to replace this.observations - past dedup state no longer applies.
        this.addedObservationKeys.clear();

        if (reportData && artifactType) {
            this.applyArtifactReport(reportData, artifactType);
        } else {
            this.artifactService.getArtifact$(artifactId).pipe(take(1)).subscribe((artifact) => {
                this.applyArtifactReport(reportData || artifact.data.reportData, artifact.artifactType);
            });
        }
    }

    private getAnalysisResults(reportData: any, artifactType: ArtifactType): any {
        if (artifactType === WellKnownArtifactTypes.MobileAccessibilityAnalysis) {
            return reportData?.a11yResults;
        }
        if (artifactType === WellKnownArtifactTypes.ContentAnalysis) {
            return reportData?.axeResults;
        }
        return undefined;
    }

    private handleFactSessionUpdate(session: FactSessionInfo): void {
        const processInfo: FactSessionInfo['processInfo'] = Object.fromEntries(
            Object.entries(session.processInfo).map(([id, process]) => {
                const progress = process.factProcessProgress;
                const isUninitialized = (progress?.progress?.current ?? 0) === 0
                    && (progress?.message === 'Uninitialized'
                        || progress?.userMessage?.code === 'Fact.process.progress.uninitialized');
                if (!isUninitialized || !this.analysisProgress) {
                    return [id, process];
                }
                return [id, {
                    ...process,
                    factProcessProgress: {
                        progress: {current: this.analysisProgress.percentage / 100},
                        message: this.analysisProgress.message
                    }
                }];
            })
        );
        this.factSessionInfo = {...session, processInfo};
        const processes = Object.values(processInfo);
        const process = processes.find(candidate => candidate.state === 'Running') || processes[0];
        const progress = process?.factProcessProgress;

        if (progress) {
            this.analysisProgress = {
                message: progress.userMessage?.fallback || progress.message || this.analysisProgress?.message || 'Analyzing',
                percentage: (progress.progress?.current ?? 0) * 100
            };
        }
        // Only Failed provides an error; artifact availability determines success for other terminal states.
        if (session.state === 'Failed') {
            const failedProcess = processes.find(candidate => candidate.state === 'Failed');
            const failure = failedProcess?.factProcessProgress;
            this.analysisError = failure?.userMessage?.fallback
                || failure?.message
                || 'The analysis could not be completed.';
        }
    }

    private applyArtifactReport(reportData: any, artifactType: ArtifactType): void {
        const analysisResults = this.getAnalysisResults(reportData, artifactType);
        if (!analysisResults) {
            return;
        }

        this.displayedAnalysisArtifactType = artifactType;
        this.reportData = reportData;
        this.observations = {
            violations: this.prepareObservationRules(analysisResults.violations, artifactType),
            inapplicable: this.prepareObservationRules(analysisResults.inapplicable, artifactType),
            passes: this.prepareObservationRules(analysisResults.passes, artifactType)
        };
        this.updateFilter(this.filterObservationsForAuditActivity);
        this.analysisUrl = analysisResults.url;
        this.computeStats();
    }

    private prepareObservationRules(rules: any[] | undefined, artifactType: ArtifactType): any[] {
        const availableRules = rules || [];
        if (artifactType !== WellKnownArtifactTypes.MobileAccessibilityAnalysis) {
            return availableRules;
        }
        return availableRules.map(rule => ({
            ...rule,
            help: rule.help || rule.description || rule.id
        }));
    }


    updateActivityOptions(specs?: { [id: string]: any }): void {
        if (!this.hasAuditContext) {
            this.activityOptions = [];
            this.selectedActivities = [];
            return;
        }

        this.activityOptions = Object.values(specs || {})
            .sort((a, b) =>
                a.displayId.localeCompare(b.displayId, undefined, {numeric: true})
            )
            .map(specObj => {
                const tags = mapSpecLabelsToAxeLabels(specObj.labels);

                const digits = specObj.displayId.split('.').slice(-3).join('');
                const matchingTag = tags.find(tag => tag.includes(digits));

                if (!matchingTag) {
                    return null;
                }

                return {
                    label: `${specObj.displayId}: ${specObj.title}`,
                    value: matchingTag
                };
            })
            .filter(item => item !== null);
    }

    updateFilter(doFilter: boolean) {
        this.filterObservationsForAuditActivity = doFilter;
        this.filteredObservations = this.filterObservations(this.observations);
    }

    ngOnDestroy() {
    }

    hasFactSessionProcesses(): boolean {
        return Object.keys(this.factSessionInfo?.processInfo ?? {}).length > 0;
    }

    showRelatedFact(fact: FactId): void {
        if (fact.artifactId) {
            this.store.setValue('TODO.shownA11yReport', fact.artifactId);
        }
    }

    mapSpecLabelsToAxeLabels(specLabels: Tag[]) {
        let result: any[] = [];
        specLabels.forEach(label => {
            if (Object.keys(label)[0] === 'wcag_level') {
                result.push('wcag' + label['wcag_level']);
            } else if (Object.keys(label)[0] === 'wcag_sc') {
                result.push('wcag' + label['wcag_sc'].replaceAll('.', ''));
            }
        });
        return result;
    };


    filterObservations(observations: WcagAnalysisResults) {
        if (!observations || !this.canUseAuditFilters || !this.filterObservationsForAuditActivity || !this.activeAuditActivitySpec) {
            return observations;
        }

        const axeLabels = this.mapSpecLabelsToAxeLabels(this.activeAuditActivitySpec?.labels || []);

        // Filter each property (violations, inapplicable, passes)
        return {
            violations: observations.violations.filter(violation =>
                violation.tags.some((tag: any) => axeLabels.includes(tag))
            ),
            inapplicable: observations.inapplicable.filter(inapplicable =>
                inapplicable.tags.some((tag: any) => axeLabels.includes(tag))
            ),
            passes: observations.passes.filter(pass =>
                pass.tags.some((tag: any) => axeLabels.includes(tag))
            )
        };
    }

    startAnalysis() {
        const mobileAnalysis = this.isMobileAnalysis;
        if (!this.canStartAnalysis) {
            if (mobileAnalysis && !this.mobileAnalysisUnavailableMessage) {
                this.analysisError = 'Mobile analysis requires an active Android or iOS device console.';
            }
            return;
        }

        this.clearObservationSelection();
        this.analyzing = true;
        this.analysisError = '';
        this.preparingMobileExpedition = mobileAnalysis;
        this.analysisDeviceId = mobileAnalysis ? this.activeDeviceId : undefined;
        this.analysisExpeditionId = mobileAnalysis ? undefined : this.activeExpedition?.id;
        this.temporaryMobileExpeditionId = undefined;
        this.factSessionInfo = undefined;

        //clear previous result
        this.observations = null;
        this.filteredObservations = null;
        this.stats = null;
        let analysisSubscription = null;
        let expeditionLockHeld = false;
        let analysisExpedition: MobileAnalysisExpedition | undefined;
        let lastAnalysisArtifactId: ArtifactId | undefined;
        const previousActiveExpedition = this.workbenchContextService.getCurrentWorkbenchContext()?.expeditionContext?.activeExpedition;
        const analysisArtifactType = mobileAnalysis
            ? WellKnownArtifactTypes.MobileAccessibilityAnalysis
            : WellKnownArtifactTypes.ContentAnalysis;
        this.displayedAnalysisArtifactType = analysisArtifactType;

        const defaultAnalysisProgress = {
            message: mobileAnalysis ? 'Preparing Mobile Analysis...' : 'Starting WCAG Analysis',
            percentage: 1
        };
        this.analysisProgress = defaultAnalysisProgress;

        const haltSubscription = this.forceHaltAnalysis.pipe(
            filter(halt => halt === true),
            take(1),
        ).subscribe(() => {
            // Immediately unsubscribe from any ongoing analysis subscription
            if (analysisSubscription) {
                analysisSubscription.unsubscribe();
            }
            this.factSessionInfo = undefined;
            this.analyzing = false;
            this.analysisProgress = undefined;
            this.forceHaltAnalysis.next(false);
        });

        const factRequest: FactRequest = mobileAnalysis
            ? {
                factType: {artifactType: WellKnownArtifactTypes.MobileAccessibilityAnalysis}
            }
            : {
                factType: {artifactType: WellKnownArtifactTypes.ContentAnalysis},
                params: {
                    value: {
                        filter: {
                            mode: this.analysisMode,
                            levels: this.selectedA11yLevels,
                            tags: this.hasAuditContext ? this.selectedActivities : [],
                        },
                        addPassesScreenshots: false, // Improves analysis speed by omitting screenshots for passed nodes
                        language: this.selectedLanguage
                    }
                }
            };

        const expedition$ = mobileAnalysis
            ? this.ensureMobileAnalysisExpedition$()
            : of({expedition: this.activeExpedition!, ownedByAnalysis: false});

        analysisSubscription = expedition$.pipe(
            switchMap(expeditionContext => this.activateAnalysisExpedition$(expeditionContext)),
            tap(expeditionContext => {
                analysisExpedition = expeditionContext;
                this.analysisExpeditionId = expeditionContext.expedition.id;
                this.preparingMobileExpedition = false;
                this.analysisProgress = {
                    message: mobileAnalysis ? 'Starting Mobile Accessibility Analysis...' : 'Starting WCAG Analysis',
                    percentage: 1
                };
                this.workbenchContextService.lockActiveExpedition();
                expeditionLockHeld = true;
            }),
            switchMap(expeditionContext => mobileAnalysis
                ? this.generateMobileAnalysisArtifact$(expeditionContext.expedition.id, factRequest)
                : this.analyzeWebState$(expeditionContext.expedition.id, factRequest, analysisArtifactType)),
            untilDestroyed(this),
            finalize(() => {
                if (expeditionLockHeld) {
                    this.workbenchContextService.unlockActiveExpedition();
                }
                this.cleanupAnalysisExpedition(analysisExpedition, previousActiveExpedition);
                this.preparingMobileExpedition = false;
                this.analysisDeviceId = undefined;
                this.analysisExpeditionId = undefined;
                haltSubscription.unsubscribe();
            })
        ).subscribe({
                next: (a11yArtifact) => {
                    if (a11yArtifact) {
                        lastAnalysisArtifactId = a11yArtifact.id;
                    } else {
                        console.error('A11y Report artifact not found');
                    }
                },
                complete: () => {
                    this.analysisProgress = undefined;
                    this.analyzing = false;
                    if (lastAnalysisArtifactId) {
                        this.store.setValue('TODO.shownA11yReport', lastAnalysisArtifactId);
                    }
                },
                error: err => {
                    if (err.name === 'TimeoutError' && !this.observations) {
                        this.analysisError = 'Analysis timed out';
                    } else if (!this.analysisError) {
                        this.analysisError = 'The analysis could not be completed. Please try again.';
                    }
                    console.error('Error while starting analysis', err);
                    this.factSessionInfo = undefined;
                    this.analysisProgress = undefined;
                    this.analyzing = false;
                }
            });
    }

    private generateMobileAnalysisArtifact$(browserSessionId: ExpeditionId, factRequest: FactRequest): Observable<Artifact> {
        const goal: ScopedFactRequest = {
            request: factRequest,
            scope: {
                type: 'BrowserSessionScope',
                browserSessionId
            }
        };

        return this.factService.generateArtifactUpdates(
            this.projectId,
            goal,
            FactGenerationMode.Query,
            WellKnownArtifactTypes.MobileAccessibilityAnalysis,
            session => this.handleFactSessionUpdate(session)
        ).pipe(
            tap(artifact => {
                this.reportData = artifact.data.reportData;
                this.onArtifactUpdate(
                    artifact.data.reportData,
                    artifact.id,
                    WellKnownArtifactTypes.MobileAccessibilityAnalysis
                );
            }),
            timeout(180000) // 3 minutes without any new results
        );
    }

    private analyzeWebState$(
        browserSessionId: ExpeditionId,
        factRequest: FactRequest,
        analysisArtifactType: ArtifactType
    ): Observable<Artifact> {
        return this.browserSessionService.createStateV2(browserSessionId, [factRequest]).pipe(
            take(1),
            mergeMap(stateCreationInfo => this.factService.observeArtifactUpdates(
                this.projectId,
                stateCreationInfo.factSessionId,
                analysisArtifactType,
                session => this.handleFactSessionUpdate(session)
            ).pipe(
                tap(artifact => {
                    this.reportData = artifact.data.reportData;
                    this.onArtifactUpdate(artifact.data.reportData, artifact.id, analysisArtifactType);
                }),
                timeout(180000) // 3 minutes without any new results
            ))
        );
    }

    private ensureMobileAnalysisExpedition$(): Observable<MobileAnalysisExpedition> {
        const context = this.workbenchContextService.getCurrentWorkbenchContext();
        const deviceId = context?.deviceContext?.activeDevice;
        const activeConsole = context?.deviceContext?.activeConsole;
        const device = this.activeDevice;

        if (!deviceId || !activeConsole || device?.id !== deviceId
            || device?.platform.getPlatformFamily() !== 'mobile') {
            return throwError(() => new Error('Mobile analysis requires an active Android or iOS device console.'));
        }

        return this.workbenchExpeditionService.getActiveExpeditionsInProject$({deviceId}).pipe(
            take(1),
            switchMap(expeditions => {
                const existingExpedition = expeditions.find(expedition => expedition.optDeviceId === deviceId);
                if (existingExpedition) {
                    return this.waitForMobileExpeditionReady$(existingExpedition.id, deviceId).pipe(
                        map(expedition => ({expedition, ownedByAnalysis: false}))
                    );
                }

                return this.deviceService.getDevice$(deviceId).pipe(
                    take(1),
                    switchMap(currentDevice => {
                        const appiumBrowser = this.findAppiumBrowser(currentDevice);
                        if (!appiumBrowser) {
                            return throwError(() => new Error(
                                'Mobile analysis could not start because this device does not provide an Appium automation capability.'
                            ));
                        }

                        const activeVehicles = currentDevice.properties[DevicePropertyNames.ActiveVehicles] || [];
                        const maxCapacity = currentDevice.properties[DevicePropertyNames.MaxExpeditionCapacity];
                        if (typeof maxCapacity === 'number' && activeVehicles.length >= maxCapacity) {
                            return throwError(() => new Error(
                                'Mobile analysis could not start because this device’s automation capacity is occupied. Stop an existing incompatible or stale session and try again.'
                            ));
                        }

                        return this.browserSessionService.startBrowserExpeditionOnDevice$(
                            this.projectId,
                            deviceId,
                            appiumBrowser
                        ).pipe(
                            tap(expeditionId => this.temporaryMobileExpeditionId = expeditionId),
                            switchMap(expeditionId => this.waitForMobileExpeditionReady$(expeditionId, deviceId)),
                            map(expedition => ({expedition, ownedByAnalysis: true}))
                        );
                    })
                );
            })
        );
    }

    private findAppiumBrowser(device: Device): Browser | undefined {
        const advertisedBrowsers = (device.properties[DevicePropertyNames.MachineBrowsers] || [])
            .map(browser => browser instanceof Browser ? browser : browserFromBackendObject(browser));
        return advertisedBrowsers.find(browser => browser.browserType.toLowerCase() === 'appium');
    }

    private waitForMobileExpeditionReady$(expeditionId: ExpeditionId, deviceId: DeviceId): Observable<BrowserSessionInfo> {
        const readyStates = ['leased', 'ready', 'running'];
        return this.browserSessionService.getBrowserSessionInfo$(expeditionId).pipe(
            filter(expedition => expedition.optDeviceId === deviceId
                && readyStates.includes(expedition.sessionState.toLowerCase())),
            take(1),
            timeout(120000) // 2 minutes timeout
        );
    }

    private activateAnalysisExpedition$(analysisExpedition: MobileAnalysisExpedition): Observable<MobileAnalysisExpedition> {
        const currentExpedition = this.workbenchContextService.getCurrentWorkbenchContext()?.expeditionContext?.activeExpedition;
        this.analysisExpeditionId = analysisExpedition.expedition.id;
        this.activeExpedition = analysisExpedition.expedition;

        if (currentExpedition?.id === analysisExpedition.expedition.id) {
            return of(analysisExpedition);
        }

        this.workbenchContextService.setContextActiveExpedition(analysisExpedition.expedition);
        return this.workbenchContextService.subscribeWorkbenchContext$().pipe(
            filter(context => context.expeditionContext?.activeExpedition?.id === analysisExpedition.expedition.id),
            take(1),
            map(() => analysisExpedition)
        );
    }

    private cleanupAnalysisExpedition(analysisExpedition: MobileAnalysisExpedition | undefined,
                                      previousActiveExpedition: BrowserSessionInfo | undefined): void {
        const temporaryExpeditionId = analysisExpedition?.ownedByAnalysis
            ? analysisExpedition.expedition.id
            : this.temporaryMobileExpeditionId;
        const analysisExpeditionId = analysisExpedition?.expedition.id || temporaryExpeditionId;
        this.temporaryMobileExpeditionId = undefined;

        const restorePreviousExpedition = () => {
            if (!analysisExpeditionId || previousActiveExpedition?.id === analysisExpeditionId) {
                return;
            }
            const currentExpedition = this.workbenchContextService.getCurrentWorkbenchContext()?.expeditionContext?.activeExpedition;
            if (!currentExpedition || currentExpedition.id === analysisExpeditionId) {
                this.restoringAnalysisExpedition = true;
                this.restoredAnalysisExpeditionId = previousActiveExpedition?.id;
                this.workbenchContextService.setContextActiveExpedition(previousActiveExpedition);
            }
        };

        if (!temporaryExpeditionId) {
            restorePreviousExpedition();
            return;
        }

        this.browserSessionService.stopExpedition$(temporaryExpeditionId).pipe(
            take(1),
            finalize(restorePreviousExpedition)
        ).subscribe({
            error: error => console.error('Could not stop temporary mobile analysis expedition', error)
        });
    }

    computeStats() {
        if (!this.observations?.violations) {
            return;
        }


        // Build the violation counts
        const violationStats = {
            critical: 0,
            serious: 0,
            moderate: 0,
            minor: 0,
            total: 0
        };

        this.observations.violations.forEach(violation => {
            violationStats[violation.impact]++;
            violationStats.total++;
        });

        // Compute overall total = sum of violations, passed, inapplicable counts
        const passesTotal = this.observations.passes?.length || 0;
        const inapplicableTotal = this.observations.inapplicable?.length || 0;

        // Compute overall total = violations + passed + inapplicable
        const totalObservations = violationStats.total + passesTotal + inapplicableTotal;

        this.stats = {
            violation: violationStats,
            passes: {total: passesTotal},
            inapplicable: {total: inapplicableTotal},
            totalObservations
        };
    }

    showObservation(observation, node, target) {
        this.activeTarget = node;
        this.activeObservation = observation;
        this.activeElementStateInfos = undefined;
        this.activeElementScreenshotMeta = undefined;

        this.activeElement = this.findElementForNode(node, target);

        this.updateActiveObservationAssessmentTasks();

        if (this.supportsInteractiveDomHighlight() && !!this.activeElement?.fullBoundingBox) {
            if (this.activeElement.elementScreenshot) {
                // these are relative to the entire page.
                const {xRel, yRel, widthRel, heightRel} = this.activeElement.fullBoundingBox;

                this.imageService.getImageMetadata(this.activeElement.elementScreenshot).subscribe(meta => {
                    this.activeElementScreenshotMeta = meta || undefined;

                    // as the screenshot is also relative to the page, we need to calculate the relative position of the element in the screenshot.
                    const {xRel: scrXRel, yRel: scrYRel, widthRel: scrWidthRel, heightRel: scrHeightRel} = meta?.position || {xRel: 0, yRel: 0, widthRel: 1, heightRel: 1};

                    // calculate the relative position of the element in the screenshot
                    const xRelInScr = xRel - scrXRel;
                    const yRelInScr = (yRel - scrYRel) * (1 / scrHeightRel);
                    const heightRelInScr = heightRel * (1 / scrHeightRel);
                    const widthRelInScr = widthRel * (1 / scrWidthRel);

                    this.activeElementStateInfos = [new HighlightedElement({x: xRelInScr, y: yRelInScr, width: widthRelInScr, height: heightRelInScr}, false, 'red', true, true)];
                });
            }
        }

        this.activeTargetFailureSummary = this.createFailureSummaryHtml();
        if (this.supportsInteractiveDomHighlight() && target) {
            this.workbenchExpeditionService.sendCDPCommand('Runtime.evaluate', {expression: `window.WebmateInfusion.highlightElements('${target}', {'scrollFirstIntoView': true, 'clearHudItems': true});`}).subscribe({
                error: err => {
                    console.error('Error while highlighting element', err);
                }
            });
        }
    }

    private getSelectorDisplayValue(selector?: ElementSelector): string | undefined {
        if (!selector) {
            return undefined;
        }
        if (selector.type === 'CssSelector') {
            return selector.selector;
        }
        const strategy = selector.strategy.type === 'AccessibilityId' ? 'accessibility id' : 'id';
        return `${strategy}: ${selector.value}`;
    }

    private readonly displayedElementProperties: Array<[string, string]> = [
        ['class', 'Class'],
        ['resourceId', 'Resource ID'],
        ['value', 'Value']
    ];

    private getElementInformation(element?: ElementDescriptor): Array<{label: string; value: string}> {
        if (!element) {
            return [];
        }
        const elementClass = element.properties?.['class']?.trim();
        const elementType = element.properties?.['type']?.trim() || (!elementClass ? element.tagName?.trim() : undefined);
        const information: Array<{label: string; value: string}> = elementType ? [{label: 'Type', value: elementType}] : [];
        this.displayedElementProperties.forEach(([propertyName, label]) => {
            const value = element.properties?.[propertyName]?.trim();
            if (value) {
                information.push({label, value});
            }
        });
        return information;
    }

    private findElementForNode(node: any, target?: string): ElementDescriptor | undefined {
        const rawElements: any[] = this.reportData?.elements || [];
        const isMobile = (this.displayedAnalysisArtifactType || this.activeAnalysisArtifactType)
            === WellKnownArtifactTypes.MobileAccessibilityAnalysis;

        if (isMobile) {
            const evidence = node?.elementEvidenceId !== undefined
                ? rawElements.find(entry => entry?.elementEvidenceId === node.elementEvidenceId)
                : rawElements.find(entry => node?.nodeId !== undefined && entry?.nodeId === node.nodeId);
            return evidence?.element;
        }

        const elements: ElementDescriptor[] = rawElements;
        return elements.find(element => this.getSelectorDisplayValue(element.mainSelector) === target);
    }

    private updateActiveObservationAssessmentTasks(): void {
        const affectedSpecs = this.canShowRelatedTasks && this.auditSession && this.activeObservation
            ? this.auditSession.findSpecsForAxeLabels(this.activeObservation.tags || [])
            : undefined;

        this.activeObservationAssessmentTasks = affectedSpecs
            ? this.auditSession?.findAsessmentTasksForSpecs(affectedSpecs.map(spec => spec.id))
            : undefined;
    }

    clearObservationSelection() {
        this.activeTarget = undefined;
        this.activeObservation = undefined;
        this.activeObservationAssessmentTasks = undefined;
        this.activeElement = undefined;
        this.activeElementStateInfos = undefined;
        this.activeElementScreenshotMeta = undefined;
        this.activeTargetFailureSummary = undefined;
    }

    createFailureSummaryHtml(): string {
        return this.buildFailureSummaryHtml(this.activeTarget);
    }

    private buildFailureSummaryHtml(target: any): string {
        if (!target) {
            return "";
        }

        let summary = target.failureSummary;

        if (!summary) {
            const msgs = [
                ...(target?.any?.flatMap(node => node?.failureSummary) ?? []),
                ...(target?.all?.flatMap(node => node?.message) ?? []),
                ...(target?.none?.flatMap(node => node?.message) ?? []),
            ].filter(msg => msg != null);

            if (msgs.length === 0) {
                return '';
            }

            summary = msgs.join('\n');
        }

        // Split the input string by newline characters and filter out any empty lines
        let lines = summary.split('\n').map(line => line.trim()).filter(line => line.length > 0);
        if (lines.length <= 0) {
            return summary;
        }

        lines = lines.map(l => this.escapeHtml(l));

        let result = '';
        let inList = false;

        for (const line of lines) {
            const lower = line.toLowerCase();

            const prefixes = [
                'fix any of the following',
                'fix all of the following',
                'korrigiere mindestens einen der folgenden punkte',
                'korrigiere alle der folgenden punkte',
                'corriger tous les éléments suivants',
                'corriger l’un des éléments suivants'
            ];

            if (prefixes.some(prefix => lower.startsWith(prefix))) {

                if (inList) {
                    result += '</ul>';
                    inList = false;
                }
                // header as a paragraph
                result += `<p>${line}</p>`;
            } else {

                if (!inList) {
                    result += `<ul class="repair-list">`;
                    inList = true;
                }
                result += `<li>${line}</li>`;
            }
        }

        if (inList) {
            result += '</ul>';
        }

        return result;
    }

    collectObservationInfo(rule: any = this.activeObservation, target: any = this.activeTarget): any {
        return {
            violationInfo: {
                summary: rule?.help,
                explanation: rule?.description,
                severity: rule?.impact,
                labels: rule?.tags,
            },
            targetInfo: {
                target: target?.target,
                node: target?.node,
                html: target?.html,
                failureSummary: this.buildFailureSummaryHtml(target)
            }
        };
    }

    /**
     * Builds the (target-independent) finding content for the active observation: title, severity,
     * the rendered HTML body and an optional screenshot. Reused for both the active-context finding
     * and the per-related-task findings.
     */
    private buildObservationFindingContent(): { title?: string; severity: IssueSeverity | null; findingText: string; screenshotId?: ScreenshotId } {
        return this.buildFindingContentForObservation(this.activeObservation, this.activeTarget, this.activeElement,
            this.activeObservation?.impact ? IssueSeverity.Error : null);
    }

    /**
     * Builds finding content for an arbitrary observation (rule + representative target node + resolved
     * element), reusing the same HTML-body construction as the active-context single-add flow. Severity
     * is passed in rather than derived here, since single-add and bulk-add use different mappings.
     */
    private buildFindingContentForObservation(rule: any, target: any, element: any, severity: IssueSeverity | null):
        { title?: string; severity: IssueSeverity | null; findingText: string; screenshotId?: ScreenshotId } {
        const observationInfo = this.collectObservationInfo(rule, target);
        const targetInfo = observationInfo.targetInfo;
        const violationInfo = observationInfo.violationInfo;
        let elementInfo: any[] = [];

        if (element) {
            elementInfo = [element];
        } else { //Append Partial Information even if elementInfo not available
            let elementData: any = {};
            if (targetInfo.html) {
                elementData.htmlString = targetInfo.html;
            }
            if (targetInfo?.target?.[0] && this.supportsInteractiveDomHighlight()) {
                elementData.mainSelector = {
                    selector: targetInfo.target[0],
                    type: 'CssSelector'
                };
            }
            if (elementData.htmlString || elementData.mainSelector) {
                elementInfo.push(elementData);
            }
        }

        let findingText = '';
        findingText += `
<div class="section">
<div class="finding-detail-label">Description</div>
<p>${targetInfo.failureSummary}</p>
</div>`;

        if (target?.text) {
            findingText += `
            <div class="section">
                <div class="finding-detail-label">Text Content</div>
                <p>${target.text}</p>
            </div>
            `;
        }

        if (target?.alternativeCaption) {
            findingText += `
            <div class="section">
                <div class="finding-detail-label">Suggested Alternative Text</div>
                <p>${target.alternativeCaption}</p>
            </div>
            `;
        }

        for (let el of elementInfo) {
            const selector = this.getSelectorDisplayValue(el.mainSelector);
            const elementInformation = this.getElementInformation(el);
            const elementInformationHtml = !selector && elementInformation.length > 0
                ? `
<div class="section">
<div class="finding-detail-label">Element Information</div>
${elementInformation.map(property =>
                    `<div><strong>${this.escapeHtml(property.label)}:</strong> ${this.escapeHtml(property.value)}</div>`
                ).join('')}
</div>`
                : '';
            const htmlContent = el.htmlString
                ? `
<div class="section">
<div class="finding-detail-label">HTML</div>
<pre><code>${this.escapeHtml(el.htmlString)}</code></pre>
</div>`
                : '';
            findingText += `
<div class="element">
${selector ? `
<div class="section">
<div class="finding-detail-label">Selector</div>
<pre><code>${this.escapeHtml(selector)}</code></pre>
</div>` : ''}
${elementInformationHtml}
${htmlContent}
</div>
`;
        }

        return {
            title: violationInfo.summary,
            severity,
            findingText,
            screenshotId: elementInfo?.[0]?.elementScreenshot
        };
    }

    /**
     * Creates a manual finding for the given content and enriches it with the text body and (optional)
     * screenshot. When explicit specs/targets are omitted, the finding is associated with the active
     * workbench context (active test run or active audit activity).
     */
    private createAndEnrichFinding(content: { title?: string; severity: IssueSeverity | null; findingText: string; screenshotId?: ScreenshotId },
                                   explicitAffectedSpecs?: SpecId[],
                                   explicitObservationTargets?: ObservationTarget[]): Observable<FindingId> {
        return this.workbenchTestService.createNewManualFinding(content.title, content.severity ?? undefined, undefined, explicitObservationTargets, explicitAffectedSpecs).pipe(
            switchMap(findingId => this.workbenchTestService.addTextToFinding(findingId, content.findingText).pipe(
                switchMap(() => content.screenshotId
                    ? this.workbenchTestService.addScreenshotToFinding(findingId, content.screenshotId)
                    : of(undefined)),
                map(() => findingId)
            )),
            take(1)
        );
    }

    addFinding() {
        const rule = this.activeObservation;
        this.createAndEnrichFinding(this.buildObservationFindingContent()).subscribe(() => {
            if (rule) {
                this.addedObservationKeys.add(this.getObservationKey(rule));
            }
        });
    }

    /**
     * Identifies the target a Finding created right now would attach to: the active audit activity
     * (itself already a spec x testObject pair, see AuditSession.computeAssessmentTasks) when one is
     * selected, else the active audit alone, else the active test run, else nothing. Folding this into
     * the dedup key means switching test runs/activities naturally makes observations look "not yet
     * added" again for the new target, while still remembering what was added under a target you
     * return to - a plain clear-on-change would lose that memory and risk real duplicate findings.
     */
    private getActiveTargetKey(): string {
        const context = this.workbenchContextService.getCurrentWorkbenchContext();
        const activeAudit = context?.auditContext?.activeAudit;
        if (activeAudit) {
            const activeActivity = context?.auditContext?.activeAuditActivity;
            return activeActivity ? `audit:${activeAudit}:activity:${activeActivity.id}` : `audit:${activeAudit}`;
        }
        const activeTestRun = context?.testRunContext?.activeTestRun;
        return activeTestRun ? `testRun:${activeTestRun}` : 'none';
    }

    /**
     * Dedup key for an observation: the active target plus the axe rule id plus its representative
     * target's selector (the first node with a non-empty target array - the same node the accordion
     * selection logic and the bulk content builder use), matching the pattern used for single-active-target
     * rules elsewhere.
     */
    private getObservationKey(rule: any): string {
        const node = rule?.nodes?.find(candidate => Array.isArray(candidate?.target) && candidate.target.length > 0);
        return `${this.getActiveTargetKey()}::${rule?.id ?? rule?.help}::${node?.target?.[0] ?? ''}`;
    }

    /**
     * Default mode: start (or resume) a manual test, then create the finding for it. We capture the
     * observation content at click time, then wait until the workbench context actually reflects the
     * active test run before creating the finding (the context update is applied asynchronously, so it
     * is not readable synchronously when startOrResumeAdHocTest emits).
     */
    startManualTestAndAddFinding() {
        if (this.addFindingPending) {
            return;
        }
        const content = this.buildObservationFindingContent();
        this.addFindingPending = true;
        this.workbenchTestService.startOrResumeAdHocTest().pipe(
            switchMap(() => this.workbenchContextService.subscribeWorkbenchContext$().pipe(
                filter(context => !!context.testRunContext?.activeTestRun),
                take(1)
            )),
            switchMap(() => this.createAndEnrichFinding(content)),
            take(1),
            finalize(() => this.addFindingPending = false)
        ).subscribe({
            error: err => console.error('Error starting manual test / creating finding', err)
        });
    }

    addFindingForRelatedTasks(tasks: AssessmentTask[]) {
        const auditSession = this.auditSession;
        if (!tasks?.length || !auditSession) {
            return;
        }

        const content = this.buildObservationFindingContent();
        const creations = tasks.map(task => {
            const targets = auditSession.getObservationTargetsForActivity(task);
            return this.createAndEnrichFinding(content, [task.assessedSpec], targets.length ? targets : undefined);
        });

        forkJoin(creations).subscribe({
            error: err => console.error('Error creating findings for related tasks', err)
        });
    }

    private static readonly VIOLATION_IMPACT_SEVERITY: { [impact: string]: IssueSeverity } = {
        minor: IssueSeverity.Warn,
        moderate: IssueSeverity.Warn,
        serious: IssueSeverity.Error,
        critical: IssueSeverity.Error,
    };

    /**
     * Bulk severity mapping: violations reuse the automated checker's granular impact mapping
     * (A11yChecker.scala), instead of the single-add flow's lossy binary Error/none mapping.
     * Passes/inapplicable have no single-add precedent to contradict, so they map to good/info.
     */
    private mapBulkSeverity(category: WcagAnalysisResultCategory, impact: string): IssueSeverity {
        switch (category) {
            case 'passes':
                return IssueSeverity.Good;
            case 'inapplicable':
                return IssueSeverity.Info;
            default:
                return WCAGAnalysisViewComponent.VIOLATION_IMPACT_SEVERITY[impact] ?? IssueSeverity.Warn;
        }
    }

    /**
     * Builds a bulk FindingCreationData entry for one observation, with its text (and screenshot, where
     * available) content built inline into cells/cellSequence so no follow-up append-cell calls are needed.
     */
    private buildBulkFindingCreationData(category: WcagAnalysisResultCategory, rule: any): Omit<FindingCreationData, 'testRun' | 'audit'> {
        const node = rule?.nodes?.find(candidate => Array.isArray(candidate?.target) && candidate.target.length > 0)
            || rule?.nodes?.[0];
        const target = node?.target?.[0];
        const element = this.findElementForNode(node, target);
        const severity = this.mapBulkSeverity(category, rule?.impact);
        const content = this.buildFindingContentForObservation(rule, node, element, severity);

        const cells: { [id: string]: ContentCell } = {};
        const cellSequence: ContentCellId[] = [];

        const textCellId = getRandomUUID() as ContentCellId;
        cells[textCellId] = {type: 'TextContentCell', text: content.findingText} as ContentCell;
        cellSequence.push(textCellId);

        if (content.screenshotId) {
            const screenshotCellId = getRandomUUID() as ContentCellId;
            cells[screenshotCellId] = {
                type: 'ObservationContentCell',
                observation: {type: 'ScreenshotObservation', screenshotId: content.screenshotId}
            } as ContentCell;
            cellSequence.push(screenshotCellId);
        }

        return {
            findingType: 'Manual.AdHocResult',
            title: content.title,
            severity: content.severity ?? undefined,
            issueData: {},
            cells,
            cellSequence,
        };
    }

    private getNotYetAddedRules(category: WcagAnalysisResultCategory): any[] {
        return (this.filteredObservations?.[category] || []).filter(rule => !this.isObservationAdded(rule));
    }

    hasNotYetAddedObservations(category: WcagAnalysisResultCategory): boolean {
        return this.getNotYetAddedRules(category).length > 0;
    }

    hasAnyNotYetAddedObservations(): boolean {
        return this.hasNotYetAddedObservations('violations') || this.hasNotYetAddedObservations('passes') || this.hasNotYetAddedObservations('inapplicable');
    }

    private getAllNotYetAddedRuleEntries(): Array<{ category: WcagAnalysisResultCategory; rule: any }> {
        const categories: Array<WcagAnalysisResultCategory> = ['violations', 'passes', 'inapplicable'];
        return categories.flatMap(category => this.getNotYetAddedRules(category).map(rule => ({category, rule})));
    }

    /**
     * Starts (or resumes) a manual test, waits for the workbench context to actually reflect it, then
     * bulk-creates the given entries - mirroring startManualTestAndAddFinding()'s choreography for the
     * single-add flow. Used for the default-mode "start a test and add" action.
     */
    private startAdHocTestThenBulkCreate(entries: Omit<FindingCreationData, 'testRun' | 'audit'>[]): Observable<FindingId[]> {
        return this.workbenchTestService.startOrResumeAdHocTest().pipe(
            switchMap(() => this.workbenchContextService.subscribeWorkbenchContext$().pipe(
                filter(ctx => !!ctx.testRunContext?.activeTestRun),
                take(1)
            )),
            switchMap(() => this.workbenchTestService.createFindingsBulk(entries)),
            take(1)
        );
    }

    private runBulkAdd(pendingKey: WcagAnalysisResultCategory | 'all', rules: any[], source$: Observable<FindingId[]>) {
        this.bulkAddPending[pendingKey] = true;
        source$.pipe(
            finalize(() => this.bulkAddPending[pendingKey] = false)
        ).subscribe({
            next: () => rules.forEach(rule => this.addedObservationKeys.add(this.getObservationKey(rule))),
            error: err => {
                console.error(`Error bulk-adding findings (${pendingKey})`, err);
                this.notificationService.toastErrorNotification('Failed to add observations to Findings.');
            }
        });
    }

    /**
     * "Add all to Findings" for a single category: converts every not-yet-added observation in that
     * category into a Finding via a single bulk request, attached to whatever is currently active.
     */
    addAllToFindings(category: WcagAnalysisResultCategory) {
        const rules = this.getNotYetAddedRules(category);
        if (!rules.length || this.bulkAddPending[category]) {
            return;
        }
        const entries = rules.map(rule => this.buildBulkFindingCreationData(category, rule));
        this.runBulkAdd(category, rules, this.workbenchTestService.createFindingsBulk(entries));
    }

    /**
     * Default-mode variant of addAllToFindings(): starts (or resumes) a manual test first.
     */
    startManualTestAndAddAllToFindings(category: WcagAnalysisResultCategory) {
        const rules = this.getNotYetAddedRules(category);
        if (!rules.length || this.bulkAddPending[category]) {
            return;
        }
        const entries = rules.map(rule => this.buildBulkFindingCreationData(category, rule));
        this.runBulkAdd(category, rules, this.startAdHocTestThenBulkCreate(entries));
    }

    /**
     * Page-level "Add all observations to Findings": converts every not-yet-added observation across
     * all three categories into Findings via a single bulk request, attached to whatever is currently active.
     */
    addAllObservationsToFindings() {
        const ruleEntries = this.getAllNotYetAddedRuleEntries();
        if (!ruleEntries.length || this.bulkAddPending.all) {
            return;
        }
        const entries = ruleEntries.map(({category, rule}) => this.buildBulkFindingCreationData(category, rule));
        this.runBulkAdd('all', ruleEntries.map(({rule}) => rule), this.workbenchTestService.createFindingsBulk(entries));
    }

    /**
     * Default-mode variant of addAllObservationsToFindings(): starts (or resumes) a manual test first.
     */
    startManualTestAndAddAllObservationsToFindings() {
        const ruleEntries = this.getAllNotYetAddedRuleEntries();
        if (!ruleEntries.length || this.bulkAddPending.all) {
            return;
        }
        const entries = ruleEntries.map(({category, rule}) => this.buildBulkFindingCreationData(category, rule));
        this.runBulkAdd('all', ruleEntries.map(({rule}) => rule), this.startAdHocTestThenBulkCreate(entries));
    }

    escapeHtml(htmlString: string): string {
        const tempElement = document.createElement('div');
        tempElement.textContent = htmlString; // This automatically escapes HTML
        return tempElement.innerHTML; // Get escaped HTML
    }

    protected readonly faSparkles = faSparkles;
    protected readonly faPlus = faPlus;
    protected readonly faExclamationCircle = faExclamationCircle;
    protected readonly faTimesCircle = faTimesCircle;
    protected readonly faCheckCircle = faCheckCircle;
    protected readonly faMinusCircle = faMinusCircle;
    protected readonly faInfoCircle = faInfoCircle;
    protected readonly faGlobe = faGlobe;
}

type WcagAnalysisResultCategory = 'violations' | 'passes' | 'inapplicable';
